/* eslint-disable import/no-deprecated */
import { clampToEndOfDay, isSameDate, withinLimits } from '@fullstory/util/date';
import React, { useCallback } from 'react';
import { MonthScroller } from './MonthScroller';

export interface DatepickerProps
  extends Pick<React.ComponentProps<typeof MonthScroller>, 'initialVisibleDate'> {
  /** The current selected date (highlighted via css class) */
  selectedDate: Date | null;
  /** Function called when user changes a date */
  onChange(date: Date): void;
  /** Clamps hours, seconds and minutes to end of day instead of midnight. */
  endOfDay?: boolean;
  /** Optional min date to consider a date valid */
  minDate?: Date;
  /** Optional max date to consider a date valid */
  maxDate?: Date;
  /** Optional: if ture, scroll into view */
  scrollIntoView?: boolean;
}

/**
 * @deprecated in favor of the `DateRangePicker` component. `DateRangePicker`
 * currently only supports ranges (with a start date and end date). If a single
 * point in time datepicker is needed, contact App UI so we can build it using
 * the new date picker styles.
 */
export const DeprecatedDatepicker: React.FC<DatepickerProps> = ({
  selectedDate,
  initialVisibleDate,
  onChange,
  endOfDay,
  minDate,
  maxDate,
  scrollIntoView,
}) => {
  const datePickerContainer = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null && scrollIntoView) {
        node.scrollIntoView();
      }
    },
    [scrollIntoView],
  );

  const onDateClick = useCallback(
    (d: Date) => {
      if (onChange) {
        if (endOfDay) clampToEndOfDay(d);
        onChange(d);
      }
    },
    [onChange, endOfDay],
  );

  const selected = useCallback(
    (d: Date) => !!selectedDate && isSameDate(selectedDate, d),
    [selectedDate],
  );
  const disabled = useCallback((d: Date) => !withinLimits(d, minDate, maxDate), [minDate, maxDate]);
  return (
    <div data-component="DeprecatedDatepicker" ref={datePickerContainer}>
      <MonthScroller
        disabled={disabled}
        selected={selected}
        initialVisibleDate={initialVisibleDate}
        onDateClick={onDateClick}
      />
    </div>
  );
};
